import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  AddIcon,
  ArrowBackLong,
} from 'utils/icons';
import { useLocation, useNavigate } from 'react-router-dom';

import Chip from 'components/Chip';
import ToggleButtons from '../../components/SharedComponents/ToggleButtons';
import FringesDetails from './Fringedetails';
import GroupFringes from './GroupFringes';

import { useFringeQuery } from 'utils/api/queries';
import { useFringeMutation } from 'utils/api/mutations';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';

import './styles.scss';
import { formatCurrency } from 'utils/helper';

const FringesPage = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const instanceType = queryParams.get('type') || 'budget';

  const contextualSuggestionsRef = useRef();
  const [scrollPosition, setScrollPosition] = useState(0);
  const [defaultFringe, setDefaultFringe] = useState([]);
  const [searchTxt, setSearchTxt] = useState('');

  // Context hooks
  const { activeBudget, hasEditAccess } = useHomeScreenContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // State for fringes
  const [fringes, setFringes] = useState([]);

  // Fetch Default Fringes
  const { data: default_Fringe } = useFringeQuery.useFringeDetailsQuery({
    filters: { instance_type: instanceType === 'user' ? 'global' : 'user' },
    enabled: true, // Adjust as needed
  });
  useEffect(() => {
    if (default_Fringe?.data?.data) {
      setDefaultFringe(default_Fringe.data.data);
    }
  }, [default_Fringe]);

  // Fetch budget-specific fringes
  const {
    data: fringesData,
    refetch,
    isLoading,
  } = useFringeQuery.useFringeDetailsQuery({
    filters: {
      ...(searchTxt ? { search: searchTxt } : {}),
      instance_type: instanceType,
      active_id: activeBudget,
    },
    enabled: true, // Adjust this based on your needs
  });

  useEffect(() => {
    if (fringesData?.data?.data) {
      const budgetFringes = fringesData.data.data;
      setFringes(budgetFringes);
    }
  }, [fringesData, activeBudget]);

  const goBack = () => {
    navigate(-1);
  };

  const fringeComparison = (a, b) => {
    return (
      a?.name === b?.name &&
      a?.display_name === b?.display_name &&
      a?.description === b?.description &&
      a?.abbreviation === b?.abbreviation &&
      a?.type === b?.type &&
      a?.value === b?.value &&
      a?.base_cap === b?.base_cap &&
      a?.cap_on_total === b?.cap_on_total
    );
  };

  const filteredFringes = useMemo(() => {
    if (!defaultFringe || !fringes) return [];

    // // Get all abbreviations used in budget fringes
    // const budgetAbbreviations = new Set(
    //   fringes.map(fringe => fringe.abbreviation)
    // );
    //
    // // Show defaults that DON'T have matching abbreviations in budget fringes
    // return defaultFringe.filter(
    //   defaultItem => !budgetAbbreviations.has(defaultItem.abbreviation)
    // );

    // New iteration should show the fringe suggestion if any change is done the the instantiated one
    return defaultFringe.filter(
      defaultItem =>
        !fringes.some(fringe => fringeComparison(fringe, defaultItem))
    );
  }, [fringes, defaultFringe]);
  // CREATE FRINGE MUTATION
  const { mutate: createFringe } = useFringeMutation.useCreateFringeMutation({
    onMutate: async newFringe => {
      await queryClient.cancelQueries(['fringeDetails']);
      const previousFringes = queryClient.getQueryData(['fringeDetails']);
      queryClient.setQueryData(['fringeDetails'], old => ({
        ...old,
        data: [...old.data, newFringe],
      }));
      return { previousFringes };
    },
    onError: (err, newFringe, context) => {
      queryClient.setQueryData(['fringeDetails'], context.previousFringes);
      console.error('Error creating fringe from suggestion:', err);
    },
    onSettled: () => {
      queryClient.invalidateQueries(['fringeDetails']);
    },
  });

  const handleAddSuggestion = fringeItem => {
    const newFringe = {
      ...fringeItem,
      instance_type: instanceType,
      budget_id: activeBudget,
    };

    // setFringes(prev => [...prev, newFringe]);

    createFringe(newFringe, {
      onSuccess: () => {
        refetch(); // Ensure new budget fringe (with new ID) is fetched
      },
    });
  };

  // Render suggestions bar with only default fringes
  const renderContextualSuggestions = () => (
    <div className="contextual-suggestions">
      <div className="contextual-suggestions__title">Suggestions:</div>
      <div className="contextual-suggestions__container">
        <ArrowBackIcon
          className={`back-icon ${scrollPosition > 0 ? '' : 'disabled'}`}
          onClick={() => {
            if (scrollPosition > 0) {
              setScrollPosition(Math.max(scrollPosition - 350, 0));
            }
          }}
        />
        <div
          className="contextual-suggestions__container__chip-wrapper"
          ref={contextualSuggestionsRef}
        >
          {filteredFringes.map(item => (
            <Chip
              key={item.id}
              className="suggestions-chip"
              title={item.display_name || item.abbreviation}
              icon={<AddIcon />}
              onClick={() => handleAddSuggestion(item)}
              disabled={instanceType === 'budget' && !hasEditAccess()}
            />
          ))}
        </div>
        <ArrowForwardIcon
          className={`forward-icon ${
            scrollPosition <
            (contextualSuggestionsRef.current?.scrollWidth || 0)
              ? ''
              : 'disabled'
          }`}
          onClick={() => {
            const scrollWidth =
              contextualSuggestionsRef.current?.scrollWidth || 0;
            if (scrollPosition < scrollWidth) {
              setScrollPosition(Math.min(scrollPosition + 350, scrollWidth));
            }
          }}
        />
      </div>
    </div>
  );

  const renderTotal = () => {
    const totalSum = fringes.reduce(
      (acc, curr) => acc + (Number(curr.cost) || 0),
      0
    );
    return (
      <div className="budget-total-wrapper-fringe">
        <Chip
          className="budget-total"
          title={`Fringe Total: ${formatCurrency(totalSum)}`}
        />
      </div>
    );
  };

  const [view, setView] = useState('fringes');

  const toggleButtons = (
    <ToggleButtons
      options={['Fringes', 'Groups']}
      clasname="fringes-groups"
      active={view}
      onToggle={setView}
      instanceType={instanceType}
    />
  );

  return (
    <div>
      <ArrowBackLong
        onClick={goBack}
        className="print-actions__back-icon"
        style={{
          marginTop: '0.6rem',
          cursor: 'pointer',
        }}
      />

      {/* <AnimatePresence mode="sync"> */}
      <div className="fringe-groups-wrapper">
        {/* <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: view === 'fringes' ? 1 : 0 }}
            transition={{ duration: 0.2 }}
            style={{ display: view === 'fringes' ? 'block' : 'none' }}
          > */}
        <div
          className="fringe-view"
          style={{ display: view === 'fringes' ? 'block' : 'none' }}
        >
          <FringesDetails
            instanceType={instanceType}
            fringes={fringes || []}
            refetch={refetch}
            className="fringe"
            searchTxt={searchTxt}
            setSearchTxt={setSearchTxt}
            isParentLoading={isLoading}
            toggleButtons={toggleButtons}
          />

          <div className="contextual-suggestions-container">
            {renderContextualSuggestions()}
            {renderTotal()}
          </div>
        </div>
        {/* </motion.div> */}

        {/* <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: view === 'groups' ? 1 : 0 }}
            transition={{ duration: 0.2 }}
            style={{ display: view === 'groups' ? 'block' : 'none' }}
          > */}
        <div
          className="group-view"
          style={{ display: view === 'groups' ? 'block' : 'none' }}
        >
          <GroupFringes
            className="groups"
            instanceType={instanceType}
            toggleButtons={toggleButtons}
          />
        </div>
        {/* </motion.div> */}
      </div>
      {/* </AnimatePresence> */}
    </div>
  );
};

export default FringesPage;
